import { useLocations } from "../../api/admin/RegionManagementAPI";
import useEntities from "../../api/otl/OtlEntitiesAPI";
import { CustomTooltip, CustomComponent } from "../common/Tooltip";
import isEmpty from "../../validation/is-empty";
import { InformationTooltipHtml } from "./PostContractButtons.components";
import PropTypes from "prop-types";
import Spinner from "../common/Spinner";
import { useEffect, useState } from "react";
import DragDropUploadFile from "../common/generic-upload-button/DragDropUploadFile";
import { updateFieldErrors } from "../../api/post-contract/PostContractFormAPI";
import { NestedDropDownComponent } from "../nested-drop-down/NestedDropDownComponent";
import { ModalCompany } from "../project-create/CreateProject.components";
import CreateProjectSelectClientCompany from "../project-create/CreateProjectSelectClientCompany";

LocationsDropDown.propTypes = {
  field: PropTypes.object,
  fields: PropTypes.array,
  fieldErrors: PropTypes.array,
  setFields: PropTypes.func,
  setSaveDisabled: PropTypes.func,
};
export function LocationsDropDown(props) {
  const { field } = props;
  const { fields } = props;
  const { fieldErrors } = props;
  const { setFields } = props;
  const { setSaveDisabled } = props;

  // get locations from OTL
  const { locations } = useLocations(true);

  //Disable Save button till all the location dropdowns loaded
  setSaveDisabled(true);
  if (locations.loading) {
    return (
      <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
        <Spinner width="30px" />
        {"Loading " + field.key + "..."}
      </div>
    );
  }

  // They key for this field is required
  const key = field.key;

  let options = [];

  //Load all regions from location object
  if (key === "Region") {
    // Create a unique list of values based on the key
    const uniqueRegions = Array.from(
      new Set(locations.data.map((loc) => loc.region))
    );
    options = uniqueRegions.map((region) => {
      return {
        id: region,
        value: region,
        name: region,
      };
    });
    options.unshift({ id: "SelectRegion", value: "", name: "Select Region" });
    setSaveDisabled(false);
  }

  //Load all countries from location object by Region Selected
  if (key === "Country") {
    // Get the selected region
    const selectedRegion = findNodeByValue(fields, "key", "Region").value;
    const countries = locations.data.filter((d) => d.region === selectedRegion);
    const uniqueCountries = Array.from(
      new Set(countries.map((loc) => loc.country))
    );
    // Create a unique list of values based on the key
    options = uniqueCountries.map((country) => {
      return {
        id: country,
        value: country,
        name: country,
      };
    });
    options.unshift({ id: "SelectCountry", value: "", name: "Select Country" });
    setSaveDisabled(false);
  }

  //Load all Cities from location object by Region and Country Selected
  if (key === "City") {
    const selectedRegion = findNodeByValue(fields, "key", "Region").value;
    const selectedCountry = findNodeByValue(fields, "key", "Country").value;
    const cities = locations.data.filter(
      (d) => d.country === selectedCountry && d.region === selectedRegion
    );

    options = cities.map((loc) => {
      return {
        id: loc.city,
        value: loc.city,
        name: loc.city,
      };
    });
    options.unshift({ id: "SelectCity", value: "", name: "Select City" });
    setSaveDisabled(false);
  }

  return (
    <>
      {!isEmpty(field.name) && (
        <div>
          <div className="pcf-element-item">
            {field.name}
            {!isEmpty(field.toolTip) && (
              <CustomTooltip
                tooltipHtml={<CustomComponent htmlContent={field.toolTip} />}
                tooltipIcon={
                  <InformationTooltipHtml key={"tooltip-" + field.id} />
                }
                iconText={field.name}
              />
            )}
          </div>
          <ValidationMessage field={field} fieldErrors={fieldErrors} />
        </div>
      )}
      <select
        id={"pcf-dropdown-" + field.id}
        name={field.name}
        className="pcf-element-item"
        value={field.value}
        onChange={(e) => {
          let temp = [...fields];
          //This function will find the node in the deeply nested json by key and value
          //This is especially usefull in "table" type datatype
          findNodeByValue(temp, "id", field.id).value = e.target.value;
          if (field.key === "Region") {
            findNodeByValue(temp, "key", "Country").value = "";
            findNodeByValue(temp, "key", "City").value = "";
          }
          setFields(temp);
        }}
      >
        {options?.map((option) => {
          return (
            <option key={option.id} value={option.value}>
              {option.name}
            </option>
          );
        })}
      </select>
      {isEmpty(field.name) && (
        <ValidationMessage field={field} fieldErrors={fieldErrors} />
      )}
    </>
  );
}

//This function will find the node in the deeply nested json by key and value
export function findNodeByValue(obj, targetKey, targetValue) {
  // Check if the current object contains the target key and value
  if (obj && typeof obj === "object" && obj[targetKey] === targetValue) {
    return obj; // Found the node
  }

  // Iterate through object properties
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const value = obj[key];

      // If the value is an object or an array, recurse
      if (typeof value === "object" && value !== null) {
        const found = findNodeByValue(value, targetKey, targetValue);
        if (found) {
          return found; // Return if found in a nested structure
        }
      }
    }
  }

  return null; // Node not found
}

ValidationMessage.propTypes = {
  field: PropTypes.object,
  fieldErrors: PropTypes.array,
};

export function ValidationMessage(props) {
  const { field, fieldErrors } = props;
  if (
    field.isMandatory === true &&
    (fieldErrors.includes(field.key) || fieldErrors.includes(field.name))
  ) {
    return (
      <div className="pcf-validation-message">
        {isEmpty(field.name) ? "Required Field" : field.name + " is Required"}{" "}
      </div>
    );
  } else {
    return null;
  }
}

FileUpload.propTypes = {
  field: PropTypes.object,
  fieldErrors: PropTypes.array,
  setFieldErrors: PropTypes.func,
  fields: PropTypes.array,
  setFields: PropTypes.func,
};

export function FileUpload(props) {
  const { setFieldErrors, fieldErrors, field, fields, setFields } = props;
  const [highlight, setHighlight] = useState(false);
  const [error, setError] = useState({});
  const [inputKey, setInputKey] = useState(0);
  const [file, setFile] = useState([]);

  useEffect(() => {
    // Add file to the set of fields
    if (!isEmpty(file)) {
      setFields((prevFields) => {
        const updatedFields = prevFields.map((f) =>
          f.id === field.id ? { ...f, value: file[0] } : f
        );
        return updatedFields;
      });
    }
  }, [file, field.id, setFields]);

  useEffect(() => {
    // Update field errors when there is any error with file
    if (!isEmpty(error)) {
      updateFieldErrors(fields, setFieldErrors);
    }
  }, [error, fields, setFieldErrors]);

  return (
    <div key={field.id}>
      <div className="general-row-container">
        <div className="pcu-file-display">
          <b>File:</b>{" "}
          {fields.find((f) => f.id === field.id).value?.name || "No File Added"}
        </div>
      </div>
      <div>
        <ValidationMessage field={field} fieldErrors={fieldErrors} />
      </div>
      <div className="general-row-container">
        <DragDropUploadFile
          setFile={setFile}
          allowMultiple={false}
          highlight={highlight}
          setHighlight={setHighlight}
          error={error}
          setError={setError}
          inputKey={inputKey}
          setInputKey={setInputKey}
        />
      </div>
    </div>
  );
}

EntityListDropdown.propTypes = {
  cell: PropTypes.object,
  row: PropTypes.object,
  field: PropTypes.object,
  fields: PropTypes.array,
  setFields: PropTypes.func,
  setSaveDisabled: PropTypes.func,
};
export function EntityListDropdown(props) {
  const { cell } = props;
  const { row } = props;
  const { field } = props;
  const { fields } = props;
  const { setFields } = props;
  const { setSaveDisabled } = props;

  // get locations from OTL
  const { entityList } = useEntities(true);

  //Disable Save button till all the location dropdowns loaded
  setSaveDisabled(true);
  if (entityList.loading) {
    return (
      <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
        <Spinner width="30px" />
        {"Loading " + cell.key + "..."}
      </div>
    );
  }

  return (
    <NestedDropDownComponent
      data={entityList.data}
      label={"Entity"}
      width={"300px"}
      onSelect={(value) => {
        let temp = [...fields];
        let inputTable = findById(temp, field.id);
        let inputRow = findById(inputTable.options, row.id);
        let inputCell = findById(inputRow.options, cell.id);
        inputCell.value = value;
        setFields(temp);
      }}
      defaultValue={cell.value}
    ></NestedDropDownComponent>
  );
}
function findById(array, findKey) {
  return array.find((x) => x.id === findKey);
}
/// props validation | SQ(javascript:S6774)
SelectOtlEntity.propTypes = {
  setEntity: PropTypes.func,
  value: PropTypes.string,
};
// Project Creation nested drop down for entity
export function SelectOtlEntity(props) {
  const { setEntity } = props;
  const { value } = props;
  // get locations from OTL
  const { entityList } = useEntities(true);

  if (entityList.loading) {
    return (
      <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
        <Spinner width="20px" />
      </div>
    );
  }

  return (
    <NestedDropDownComponent
      data={entityList.data}
      label={"Entity"}
      width={"450px"}
      onSelect={(value) => {
        if (value === "Select Entity") {
          setEntity("");
          return;
        }
        setEntity(value);
      }}
      defaultValue={isEmpty(value) ? "Select Entity" : value}
    ></NestedDropDownComponent>
  );
}

BenchmarkClientCompany.propTypes = {
  field: PropTypes.object,
  fields: PropTypes.array,
  setFields: PropTypes.func,
};
export function BenchmarkClientCompany(props) {
  const { field, fields, setFields } = props;
  const [clientCompany, setClientCompany] = useState({});

  /* eslint-disable */
  useEffect(() => {
    let temp = [...fields];
    temp.find((f) => f.id === field.id).value = clientCompany?.id;
    setFields(temp);
  }, [clientCompany]);
  /* eslint-enable */
  return (
    <ModalCompany
      Component={CreateProjectSelectClientCompany}
      setFunction={setClientCompany}
      label={"Select Client Company"}
      company={clientCompany}
      error={{}}
      title={"Client Company"}
      isProjectCreation={true}
    />
  );
}
